import { LAYOUT_COMPONENT_TYPE, type LayoutBlock } from '@web-studio/shared-schemas';
import { BlockHiddenMarker } from './BlockHiddenMarker';
import { resolveBlockVariant } from './blocks/resolveBlockVariant';
import type { BlockRenderProps } from './types';

function isHeroBlock(block: LayoutBlock): boolean {
  if (
    block.componentType === LAYOUT_COMPONENT_TYPE.heroEditorialSplit ||
    block.componentType === LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere
  ) {
    return true;
  }
  return block.registryBlockKey?.startsWith('Hero_') ?? false;
}

function resolvePrimaryHeroId(blocks: LayoutBlock[]): string | null {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);
  const hero = ordered.find((b) => isHeroBlock(b));
  return hero?.blockId ?? null;
}

export function BlockRenderer({
  block,
  locale,
  activeBlockId,
  activeEditTarget = null,
  editable = false,
  allBlocks,
  pages,
  defaultLocale,
  supportedLocales,
  publicPathPrefix,
  pathname,
  siteMode,
  vertical,
  themeId,
  colorsetId,
  hasTastingTimeline,
  onNavigateToPageSlug,
  onLocaleChange,
}: BlockRenderProps & { activeBlockId?: string | null; allBlocks: LayoutBlock[] }) {
  const isActive = activeBlockId === block.blockId;
  const primaryHeroId = resolvePrimaryHeroId(allBlocks);
  const headingLevel =
    block.blockId === primaryHeroId ||
    block.componentType === LAYOUT_COMPONENT_TYPE.legalImprintBlock ||
    block.componentType === LAYOUT_COMPONENT_TYPE.legalPrivacyBlock
      ? 'h1'
      : 'h2';

  const props: BlockRenderProps = {
    block,
    locale,
    isActive,
    activeEditTarget: isActive ? activeEditTarget : null,
    editable,
    headingLevel,
    pages,
    defaultLocale,
    supportedLocales,
    publicPathPrefix,
    pathname,
    siteMode,
    vertical,
    themeId,
    colorsetId,
    allBlocks,
    hasTastingTimeline,
    onNavigateToPageSlug,
    onLocaleChange,
  };

  const Component = resolveBlockVariant(block.componentType, block.registryBlockKey);
  if (!Component) {
    return null;
  }

  const isDraftHidden = block.hidden === true;

  return (
    <>
      <Component {...props} />
      <BlockHiddenMarker blockId={block.blockId} hidden={isDraftHidden} />
    </>
  );
}
